// Safe, user-controlled PWA updates. The waiting worker never takes over until
// the user explicitly accepts; this avoids surprise reloads during business work.
export function registerServiceWorkerUpdates(workerUrl = "/sw.js") {
  if (!("serviceWorker" in navigator) || process.env.NODE_ENV !== "production") return;

  let updateAccepted = false;
  let banner = null;

  const showUpdatePrompt = (registration) => {
    if (!registration.waiting || !navigator.serviceWorker.controller || banner) return;

    banner = document.createElement("section");
    banner.setAttribute("role", "status");
    banner.setAttribute("aria-live", "polite");
    Object.assign(banner.style, {
      position: "fixed", zIndex: "2147483000", left: "16px", right: "16px",
      bottom: "16px", maxWidth: "560px", margin: "0 auto", padding: "16px",
      borderRadius: "14px", background: "#172033", color: "#fff",
      boxShadow: "0 8px 32px rgba(0,0,0,.28)", fontFamily: "system-ui, sans-serif",
      display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap"
    });

    const copy = document.createElement("div");
    copy.textContent = "A new version is ready. Save or finish any work in progress before updating.";
    Object.assign(copy.style, { flex: "1 1 220px", fontSize: "14px", lineHeight: "1.45" });

    const later = document.createElement("button");
    later.type = "button";
    later.textContent = "Later";
    Object.assign(later.style, {
      border: "1px solid rgba(255,255,255,.35)", borderRadius: "9px",
      padding: "9px 13px", background: "transparent", color: "#fff",
      fontWeight: "600", cursor: "pointer"
    });
    later.addEventListener("click", () => {
      banner?.remove();
      banner = null;
    });

    const update = document.createElement("button");
    update.type = "button";
    update.textContent = "Update now";
    Object.assign(update.style, {
      border: "0", borderRadius: "9px", padding: "10px 14px",
      background: "#fff", color: "#172033", fontWeight: "700", cursor: "pointer"
    });
    update.addEventListener("click", () => {
      const ready = window.confirm(
        "Updating will reload this app. Make sure all forms are saved and any payment, order, stock, or other transaction has finished. Update now?"
      );
      if (!ready) return;
      updateAccepted = true;
      update.disabled = true;
      update.textContent = "Updating…";
      registration.waiting?.postMessage({ type: "SKIP_WAITING" });
    });

    banner.append(copy, later, update);
    document.body.appendChild(banner);
  };

  window.addEventListener("load", () => {
    navigator.serviceWorker.register(workerUrl, { updateViaCache: "none" })
      .then((registration) => {
        registration.update().catch(() => {});
        if (registration.waiting) showUpdatePrompt(registration);

        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed") showUpdatePrompt(registration);
          });
        });

        // Recheck on return to an open installed app and periodically while online.
        window.addEventListener("focus", () => registration.update().catch(() => {}));
        window.setInterval(() => {
          if (navigator.onLine) registration.update().catch(() => {});
        }, 60 * 60 * 1000);
      })
      .catch(() => {
        // PWA support is best-effort and must never prevent the app from loading.
      });
  });

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!updateAccepted) return;
    window.location.reload();
  });
}
